import { supabase } from '../lib/supabase';
import { getThumbnailPath } from '../utils/imageUtils';

/**
 * Reports & Earnings Service
 *
 * Handles all operations for the corporate Reports & Earnings page:
 * - Fetching submitted job instances with timesheets
 * - Approving/Rejecting reports
 * - Handling disputes
 * - Marking as paid
 */
export const reportsEarningsService = {
  /**
   * Get all submitted job instances with their timesheets for a company
   * Includes job, service receiver, contractor, and photo data
   */
  async getSubmittedJobs(companyId, filters = {}) {
    let query = supabase
      ?.from('job_instances')
      ?.select(`
        *,
        job:jobs!inner(
          id,
          job_display_id,
          job_name,
          job_type,
          job_source,
          partner_agency_id,
          location_address,
          payment_type,
          payment_amount,
          estimated_hours,
          service_receiver_id,
          is_internal,
          client_id,
          job_site_id,
          original_address,
          client_charge_type,
          client_charge_amount,
          client_include_gst,
          client_gst_type,
          contractor_include_gst,
          contractor_gst_type,
          created_at,
          updated_at,
          service_receiver:service_receivers(id, receiver_name, receiver_type),
          partner_agency:partner_agencies(id, agency_name, contact_person, commission_rate),
          client:clients(id, client_name, contact_person, tag),
          job_site:job_sites(id, site_name, address)
        ),
        contractor:user_profiles!job_instances_assigned_contractor_fkey(
          id,
          full_name,
          email
        ),
        timesheet:timesheets(
          id,
          status,
          total_hours,
          capped_hours,
          estimated_hours,
          base_amount,
          overtime_amount,
          has_overtime_request,
          amount_earned,
          clock_in_time,
          clock_out_time,
          approved_by,
          approved_at,
          created_at
        ),
        overtime_request:overtime_requests(
          id,
          status,
          overtime_hours,
          approved_overtime_amount,
          hourly_rate
        ),
        photos:job_photos(
          id,
          photo_type,
          storage_path,
          thumbnail_path,
          uploaded_at
        ),
        additional_charges:job_instance_charges(
          id,
          charge_type,
          description,
          amount,
          include_gst,
          gst_amount,
          created_at
        )
      `)
      ?.eq('company_id', companyId)
      ?.in('status', ['submitted', 'approved', 'disputed', 'paid', 'needs_revision']);

    // Apply status filter
    if (filters?.status && filters.status !== 'all') {
      query = query?.eq('status', filters.status);
    }

    // Apply date range filter
    if (filters?.startDate) {
      query = query?.gte('scheduled_date', filters.startDate);
    }
    if (filters?.endDate) {
      query = query?.lte('scheduled_date', filters.endDate);
    }

    // Apply contractor filter
    if (filters?.contractorId) {
      query = query?.eq('assigned_contractor', filters.contractorId);
    }

    // Apply service receiver filter (legacy)
    if (filters?.serviceReceiverId) {
      query = query?.eq('job.service_receiver_id', filters.serviceReceiverId);
    }

    // Apply partner agency filter (legacy)
    if (filters?.partnerAgencyId) {
      query = query?.eq('job.partner_agency_id', filters.partnerAgencyId);
    }

    // Apply client filter (new)
    if (filters?.clientId) {
      query = query?.eq('job.client_id', filters.clientId);
    }

    // Apply job site filter (new)
    if (filters?.jobSiteId) {
      query = query?.eq('job.job_site_id', filters.jobSiteId);
    }

    // Order by most recent first
    query = query?.order('updated_at', { ascending: false });

    const { data, error } = await query;
    if (error) throw error;

    // Post-process data
    if (data) {
      for (const instance of data) {
        // Generate signed URLs for photos (both full and thumbnail)
        if (instance.photos?.length > 0) {
          instance.photos = await Promise.all(
            instance.photos.map(async (photo) => {
              const thumbnailPath = photo.thumbnail_path || getThumbnailPath(photo.storage_path);

              // Fetch both URLs in parallel
              const [fullUrlResult, thumbUrlResult] = await Promise.all([
                supabase?.storage?.from('job-photos')?.createSignedUrl(photo.storage_path, 3600),
                supabase?.storage?.from('job-photos')?.createSignedUrl(thumbnailPath, 3600),
              ]);

              const url = fullUrlResult?.data?.signedUrl;
              const thumbnailUrl = thumbUrlResult?.data?.signedUrl || url;

              return { ...photo, url, thumbnailUrl, signedUrl: url };
            })
          );
        }

        // Sort timesheets by created_at descending and keep only the most recent
        // This handles cases where multiple timesheets exist from resubmissions
        if (instance.timesheet?.length > 1) {
          instance.timesheet.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
          instance.timesheet = [instance.timesheet[0]];
        }
      }
    }

    return data || [];
  },

  /**
   * Get counts for each status tab
   */
  async getStatusCounts(companyId) {
    const { data, error } = await supabase
      ?.from('job_instances')
      ?.select('status')
      ?.eq('company_id', companyId)
      ?.in('status', ['submitted', 'approved', 'disputed', 'paid', 'needs_revision']);

    if (error) throw error;

    const counts = {
      all: data?.length || 0,
      submitted: 0,
      approved: 0,
      disputed: 0,
      paid: 0,
      needs_revision: 0
    };

    data?.forEach(item => {
      if (counts[item.status] !== undefined) {
        counts[item.status]++;
      }
    });

    return counts;
  },

  /**
   * Get detailed report for a specific job instance
   * Includes all photos, notes, questionnaire responses, checklist data
   */
  async getReportDetails(jobInstanceId) {
    const { data, error } = await supabase
      ?.from('job_instances')
      ?.select(`
        *,
        job:jobs!inner(
          id,
          job_name,
          job_type,
          location_address,
          payment_type,
          payment_amount,
          questionnaire_template,
          client_id,
          job_site_id,
          original_address,
          service_receiver:service_receivers(id, receiver_name, receiver_type),
          client:clients(id, client_name, contact_person, contact_email, contact_phone, tag),
          job_site:job_sites(id, site_name, address, contact_person, contact_email, contact_phone)
        ),
        contractor:user_profiles!job_instances_assigned_contractor_fkey(
          id,
          full_name,
          email
        ),
        timesheet:timesheets(
          id,
          status,
          total_hours,
          capped_hours,
          estimated_hours,
          base_amount,
          overtime_amount,
          has_overtime_request,
          amount_earned,
          clock_in_time,
          clock_out_time,
          approved_by,
          approved_at,
          created_at
        ),
        overtime_request:overtime_requests(
          id,
          status,
          overtime_hours,
          requested_overtime_amount,
          approved_overtime_amount,
          hourly_rate,
          justification,
          rejection_reason
        ),
        photos:job_photos(
          id,
          photo_type,
          storage_path,
          thumbnail_path,
          uploaded_at
        )
      `)
      ?.eq('id', jobInstanceId)
      ?.single();

    if (error) throw error;

    // Get signed URLs for photos (both full and thumbnail)
    if (data?.photos?.length > 0) {
      const photosWithUrls = await Promise.all(
        data.photos.map(async (photo) => {
          const thumbnailPath = photo.thumbnail_path || getThumbnailPath(photo.storage_path);

          // Fetch both URLs in parallel
          const [fullUrlResult, thumbUrlResult] = await Promise.all([
            supabase?.storage?.from('job-photos')?.createSignedUrl(photo.storage_path, 3600),
            supabase?.storage?.from('job-photos')?.createSignedUrl(thumbnailPath, 3600),
          ]);

          const url = fullUrlResult?.data?.signedUrl;
          const thumbnailUrl = thumbUrlResult?.data?.signedUrl || url;

          return {
            ...photo,
            url,
            thumbnailUrl,
            signedUrl: url  // For backwards compatibility
          };
        })
      );
      data.photos = photosWithUrls;
    }

    // Sort timesheets by created_at descending and keep only the most recent
    // This handles cases where multiple timesheets exist from resubmissions
    if (data?.timesheet?.length > 1) {
      data.timesheet.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      data.timesheet = [data.timesheet[0]];
    }

    return data;
  },

  /**
   * Approve a submitted report
   * Updates both job_instance and timesheet status
   */
  async approveReport(jobInstanceId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Update job instance status
    const { error: instanceError } = await supabase
      ?.from('job_instances')
      ?.update({
        status: 'approved',
        updated_at: new Date().toISOString()
      })
      ?.eq('id', jobInstanceId);

    if (instanceError) throw instanceError;

    // Update timesheet status
    const { error: timesheetError } = await supabase
      ?.from('timesheets')
      ?.update({
        status: 'approved',
        approved_by: user.id,
        approved_at: new Date().toISOString()
      })
      ?.eq('job_instance_id', jobInstanceId);

    if (timesheetError) throw timesheetError;

    return { success: true };
  },

  /**
   * Reject a submitted report
   * Sends back for revision with feedback
   */
  async rejectReport(jobInstanceId, reason) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Update job instance status
    // Try with notes field first (may not exist if migration hasn't run)
    let { error: instanceError } = await supabase
      ?.from('job_instances')
      ?.update({
        status: 'needs_revision',
        notes: reason,
        updated_at: new Date().toISOString()
      })
      ?.eq('id', jobInstanceId);

    // If notes column doesn't exist, retry without it
    if (instanceError && (instanceError.message?.includes('notes') || instanceError.message?.includes('schema cache') || instanceError.code === '42703')) {
      console.warn('notes column not found, rejecting without storing reason. Error:', instanceError.message);
      const { error: retryError } = await supabase
        ?.from('job_instances')
        ?.update({
          status: 'needs_revision',
          updated_at: new Date().toISOString()
        })
        ?.eq('id', jobInstanceId);
      instanceError = retryError;
    }

    if (instanceError) throw instanceError;

    // Update timesheet status
    const { error: timesheetError } = await supabase
      ?.from('timesheets')
      ?.update({
        status: 'rejected'
      })
      ?.eq('job_instance_id', jobInstanceId);

    if (timesheetError) throw timesheetError;

    return { success: true };
  },

  /**
   * Create a dispute for a report
   */
  async disputeReport(jobInstanceId, reason, category = 'quality') {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Store dispute info in notes field (format: [CATEGORY] reason)
    // Try with notes field first (may not exist if migration hasn't run)
    let { error } = await supabase
      ?.from('job_instances')
      ?.update({
        status: 'disputed',
        notes: `[${category.toUpperCase()}] ${reason}`,
        updated_at: new Date().toISOString()
      })
      ?.eq('id', jobInstanceId);

    // If notes column doesn't exist, retry without it
    if (error && (error.message?.includes('notes') || error.message?.includes('schema cache') || error.code === '42703')) {
      console.warn('notes column not found, disputing without storing reason. Error:', error.message);
      const { error: retryError } = await supabase
        ?.from('job_instances')
        ?.update({
          status: 'disputed',
          updated_at: new Date().toISOString()
        })
        ?.eq('id', jobInstanceId);
      error = retryError;
    }

    if (error) throw error;

    return { success: true };
  },

  /**
   * Resolve a dispute - moves back to approved status
   */
  async resolveDispute(jobInstanceId, resolution) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Append resolution to notes (may not exist if migration hasn't run)
    let { error } = await supabase
      ?.from('job_instances')
      ?.update({
        status: 'approved',
        notes: resolution ? `[RESOLVED] ${resolution}` : null,
        updated_at: new Date().toISOString()
      })
      ?.eq('id', jobInstanceId);

    // If notes column doesn't exist, retry without it
    if (error && (error.message?.includes('notes') || error.message?.includes('schema cache') || error.code === '42703')) {
      console.warn('notes column not found, resolving without storing resolution. Error:', error.message);
      const { error: retryError } = await supabase
        ?.from('job_instances')
        ?.update({
          status: 'approved',
          updated_at: new Date().toISOString()
        })
        ?.eq('id', jobInstanceId);
      error = retryError;
    }

    if (error) throw error;

    // Also update timesheet to approved
    await supabase
      ?.from('timesheets')
      ?.update({
        status: 'approved',
        approved_by: user.id,
        approved_at: new Date().toISOString()
      })
      ?.eq('job_instance_id', jobInstanceId);

    return { success: true };
  },

  /**
   * Mark one or more reports as paid
   * Updates both job_instances and timesheets status
   */
  async markAsPaid(jobInstanceIds) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const ids = Array.isArray(jobInstanceIds) ? jobInstanceIds : [jobInstanceIds];
    console.log('markAsPaid called with ids:', ids);

    // Update job instances status
    const { data: instanceData, error: instanceError } = await supabase
      ?.from('job_instances')
      ?.update({
        status: 'paid',
        updated_at: new Date().toISOString()
      })
      ?.in('id', ids)
      ?.select();

    console.log('Job instance update result:', { data: instanceData, error: instanceError });

    if (instanceError) throw instanceError;

    // Also update timesheets status to paid (if any exist)
    const { data: timesheetData, error: timesheetError } = await supabase
      ?.from('timesheets')
      ?.update({
        status: 'paid'
      })
      ?.in('job_instance_id', ids)
      ?.select();

    console.log('Timesheet update result:', { data: timesheetData, error: timesheetError });

    return { success: true, count: ids.length };
  },

  /**
   * Mark one or more reports as unpaid (revert from paid to approved)
   * Updates both job_instances and timesheets status
   */
  async markAsUnpaid(jobInstanceIds) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const ids = Array.isArray(jobInstanceIds) ? jobInstanceIds : [jobInstanceIds];

    // Update job instances status back to approved
    const { error: instanceError } = await supabase
      ?.from('job_instances')
      ?.update({
        status: 'approved',
        updated_at: new Date().toISOString()
      })
      ?.in('id', ids);

    if (instanceError) throw instanceError;

    // Also update timesheets: status back to approved, clear batch link and contractor confirmation
    await supabase
      ?.from('timesheets')
      ?.update({
        status: 'approved',
        payment_batch_id: null,
        contractor_confirmed_received: false,
        contractor_confirmed_at: null
      })
      ?.in('job_instance_id', ids);

    return { success: true, count: ids.length };
  },

  /**
   * Bulk approve multiple reports
   */
  async bulkApprove(jobInstanceIds) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const ids = Array.isArray(jobInstanceIds) ? jobInstanceIds : [jobInstanceIds];

    // Update job instances
    const { error: instanceError } = await supabase
      ?.from('job_instances')
      ?.update({
        status: 'approved',
        updated_at: new Date().toISOString()
      })
      ?.in('id', ids);

    if (instanceError) throw instanceError;

    // Update timesheets
    const { error: timesheetError } = await supabase
      ?.from('timesheets')
      ?.update({
        status: 'approved',
        approved_by: user.id,
        approved_at: new Date().toISOString()
      })
      ?.in('job_instance_id', ids);

    if (timesheetError) throw timesheetError;

    return { success: true, count: ids.length };
  },

  /**
   * Mark client payment as collected (AR - Accounts Receivable)
   * Updates job_instances client_payment_received status
   */
  async markAsCollected(jobInstanceIds) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const ids = Array.isArray(jobInstanceIds) ? jobInstanceIds : [jobInstanceIds];

    const { error } = await supabase
      ?.from('job_instances')
      ?.update({
        client_payment_received: true,
        client_payment_received_at: new Date().toISOString(),
        client_payment_received_by: user.id,
        updated_at: new Date().toISOString()
      })
      ?.in('id', ids);

    if (error) throw error;
    return { success: true, count: ids.length };
  },

  /**
   * Mark client payment as outstanding (revert from collected)
   * Updates job_instances client_payment_received status
   */
  async markAsOutstanding(jobInstanceIds) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const ids = Array.isArray(jobInstanceIds) ? jobInstanceIds : [jobInstanceIds];

    const { error } = await supabase
      ?.from('job_instances')
      ?.update({
        client_payment_received: false,
        client_payment_received_at: null,
        client_payment_received_by: null,
        updated_at: new Date().toISOString()
      })
      ?.in('id', ids);

    if (error) throw error;
    return { success: true, count: ids.length };
  },

  /**
   * Apply charge adjustment to a job instance
   * Used to correct client charge errors post-completion
   */
  async applyChargeAdjustment(jobInstanceId, adjustment, reason, includeGst = false, gstAmount = 0) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      ?.from('job_instances')
      ?.update({
        charge_adjustment: adjustment,
        charge_adjustment_reason: reason,
        charge_adjustment_include_gst: includeGst,
        charge_adjustment_gst_amount: gstAmount,
        charge_adjustment_by: user.id,
        charge_adjustment_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      ?.eq('id', jobInstanceId);

    if (error) throw error;
    return { success: true };
  },

  /**
   * Get additional charges for a job instance
   */
  async getAdditionalCharges(jobInstanceId) {
    const { data, error } = await supabase
      ?.from('job_instance_charges')
      ?.select('*')
      ?.eq('job_instance_id', jobInstanceId)
      ?.order('created_at', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  /**
   * Add an additional charge to a job instance
   */
  async addAdditionalCharge(jobInstanceId, companyId, chargeData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { charge_type, description, amount, include_gst = false, gst_amount = 0 } = chargeData;

    const { data, error } = await supabase
      ?.from('job_instance_charges')
      ?.insert({
        job_instance_id: jobInstanceId,
        company_id: companyId,
        charge_type,
        description,
        amount,
        include_gst,
        gst_amount,
        created_by: user.id
      })
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  },

  /**
   * Update an additional charge
   */
  async updateAdditionalCharge(chargeId, chargeData) {
    const { charge_type, description, amount, include_gst, gst_amount } = chargeData;

    const { data, error } = await supabase
      ?.from('job_instance_charges')
      ?.update({
        charge_type,
        description,
        amount,
        include_gst,
        gst_amount,
        updated_at: new Date().toISOString()
      })
      ?.eq('id', chargeId)
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  },

  /**
   * Delete an additional charge
   */
  async deleteAdditionalCharge(chargeId) {
    const { error } = await supabase
      ?.from('job_instance_charges')
      ?.delete()
      ?.eq('id', chargeId);

    if (error) throw error;
    return { success: true };
  },

  /**
   * Get list of contractors who have submitted jobs (for filter dropdown)
   */
  async getContractorsWithSubmissions(companyId) {
    const { data, error } = await supabase
      ?.from('job_instances')
      ?.select(`
        assigned_contractor,
        contractor:user_profiles!job_instances_assigned_contractor_fkey(
          id,
          full_name
        )
      `)
      ?.eq('company_id', companyId)
      ?.in('status', ['submitted', 'approved', 'disputed', 'paid'])
      ?.not('assigned_contractor', 'is', null);

    if (error) throw error;

    // Get unique contractors
    const contractorMap = new Map();
    data?.forEach(item => {
      if (item.contractor && !contractorMap.has(item.contractor.id)) {
        contractorMap.set(item.contractor.id, item.contractor);
      }
    });

    return Array.from(contractorMap.values());
  }
};
